import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { KpiValueItem } from '../../kpi/hooks.js';
import type { ScenarioRow } from '../../scenario/types.js';

/**
 * "Value by Day: Top 3 Scenarios & Other" — left chart tile on the
 * Daily Dashboard's bottom row. Mirrors the legacy `sec_065` widget:
 * stacked bar chart showing each day's $-value contribution from the
 * top 3 scenarios (by total value across the active range) plus an
 * "Others" bucket aggregating the rest.
 *
 * Value sourcing: per item, use `metrics[mainMetricId].value` when a
 * main metric is configured + present; otherwise sum `.value` across
 * all metrics on the item. Same fallback that the Cost Saving card +
 * Scenarios By Value table use ([kpi/hooks.ts]).
 */
const STACK_COLORS = [
  'hsl(180, 70%, 55%)', // cyan
  'hsl(220, 70%, 60%)', // blue
  'hsl(290, 60%, 60%)', // purple
  'hsl(40, 70%, 55%)', // amber (Others)
] as const;

export interface ValueByDayChartProps {
  items: ReadonlyArray<KpiValueItem>;
  rows: ReadonlyArray<ScenarioRow>;
  mainMetricId: string | null;
}

export function ValueByDayChart({ items, rows, mainMetricId }: ValueByDayChartProps): JSX.Element {
  const data = useMemo(() => buildData(items, rows, mainMetricId), [items, rows, mainMetricId]);
  return (
    <section className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-3">
      <header className="mb-2 text-sm font-medium text-foreground/80">
        Value by Day: Top 3 Scenarios &amp; Other
      </header>
      {data.rows.length === 0 ? (
        <div className="flex h-48 items-center justify-center text-sm text-foreground/40">
          No KPI data available for the active range.
        </div>
      ) : (
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={data.rows} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                interval="preserveStartEnd"
                tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 10 }}
                stroke="rgba(255,255,255,0.2)"
              />
              <YAxis
                tickFormatter={(v) => `$${shortNumber(Number(v))}`}
                tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 10 }}
                stroke="rgba(255,255,255,0.2)"
              />
              <Tooltip
                cursor={{ fill: 'rgba(255,255,255,0.06)' }}
                contentStyle={{
                  backgroundColor: 'rgba(7, 23, 48, 0.95)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 6,
                  color: '#fff',
                }}
                labelStyle={{ color: 'rgba(255,255,255,0.6)' }}
                formatter={(value: number, name) => [`$${value.toLocaleString()}`, String(name)]}
              />
              <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.65)' }} />
              {data.series.map((s, i) => (
                <Bar
                  key={s.key}
                  dataKey={s.key}
                  name={s.label}
                  stackId="value"
                  fill={STACK_COLORS[i] ?? STACK_COLORS[STACK_COLORS.length - 1]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}

interface SeriesDef {
  key: string;
  label: string;
}

interface ChartRow {
  date: string;
  [seriesKey: string]: string | number;
}

interface ChartData {
  rows: ChartRow[];
  series: SeriesDef[];
}

function buildData(
  items: ReadonlyArray<KpiValueItem>,
  rows: ReadonlyArray<ScenarioRow>,
  mainMetricId: string | null,
): ChartData {
  if (items.length === 0) return { rows: [], series: [] };
  const nameById = new Map<string, string>();
  for (const r of rows) if (r.fusionId && r.name) nameById.set(r.fusionId, r.name);
  const filteredIds = new Set(nameById.keys());

  // Per-scenario value totals + per-day per-scenario values
  const totalsByScenario = new Map<string, number>();
  const byDay = new Map<string, Map<string, number>>();
  for (const it of items) {
    if (filteredIds.size > 0 && !filteredIds.has(it.fusionScenarioId)) continue;
    let dayValue = 0;
    if (mainMetricId && it.metrics[mainMetricId]) {
      dayValue = it.metrics[mainMetricId].value ?? 0;
    } else {
      for (const c of Object.values(it.metrics)) dayValue += c.value ?? 0;
    }
    if (dayValue === 0) continue;
    totalsByScenario.set(
      it.fusionScenarioId,
      (totalsByScenario.get(it.fusionScenarioId) ?? 0) + dayValue,
    );
    const dayMap = byDay.get(it.datePart) ?? new Map<string, number>();
    dayMap.set(it.fusionScenarioId, (dayMap.get(it.fusionScenarioId) ?? 0) + dayValue);
    byDay.set(it.datePart, dayMap);
  }
  if (totalsByScenario.size === 0) return { rows: [], series: [] };

  const ranked = Array.from(totalsByScenario.entries()).sort(([, a], [, b]) => b - a);
  const top3 = ranked.slice(0, 3);
  const otherIds = new Set(ranked.slice(3).map(([id]) => id));

  const series: SeriesDef[] = top3.map(([id]) => ({
    key: id,
    label: nameById.get(id) ?? truncate(id, 18),
  }));
  if (otherIds.size > 0) series.push({ key: '__others__', label: 'Others' });

  const dayKeys = Array.from(byDay.keys()).sort();
  const chartRows: ChartRow[] = dayKeys.map((date) => {
    const row: ChartRow = { date: shortDate(date) };
    const dayMap = byDay.get(date)!;
    for (const s of series) {
      if (s.key === '__others__') {
        let sum = 0;
        for (const id of otherIds) sum += dayMap.get(id) ?? 0;
        row[s.key] = sum;
      } else {
        row[s.key] = dayMap.get(s.key) ?? 0;
      }
    }
    return row;
  });

  return { rows: chartRows, series };
}

function shortDate(iso: string): string {
  // `iso` is `YYYY-MM-DD` from the engine — render as `MM-DD` to fit
  // the legacy x-axis width without rotating labels.
  return iso.length >= 10 ? iso.slice(5) : iso;
}

function shortNumber(v: number): string {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return String(v);
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}
